import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  aplicarEstructura,
  cuadrarConTotal,
  detectarEstructura,
  elegirHoja,
  headerSignature,
  type EstructuraArchivo,
  buildRevenueImport,
  buildUnitsImport,
  guessRevenueColumns,
  guessUnitsColumns,
  looksLikeHeader,
  nextDishSalesRange,
  pickSavedMapping,
  promptAdaptarVentas,
  rangoEnPalabras,
  targetKey,
  PAYMENT_METHOD_LABELS,
  UNIDADES_TEMPLATE_HEADERS,
  VENTAS_TEMPLATE_HEADERS,
  VENTAS_TEMPLATE_ROWS,
  type DeliveryPlatform,
  type ImportTarget,
  type Period,
  type SavedImportMapping,
} from '@alexiarest/shared';
import { api } from '../api/client';
import {
  keys,
  useImportMappings,
  useImportRevenues,
  useSaveImportMapping,
  type ImportRevenuesResult,
  useMapeoVentas,
  useImportUnitsSold,
  useMenuItems,
  useRecipes,
  useSettings,
} from '../api/hooks';
import { usePeriod } from '../context/PeriodContext';
import { useLocationScope } from '../context/LocationContext';
import type { MenuItem } from '../api/types';
import { formatDate, formatMoney, formatNumber, formatPeriod, hoyLocal } from '../format';
import { CheckboxField } from './Field';
import { Drawer } from './Drawer';
import { FilasDescartadas } from './FilasDescartadas';
import { useToast } from './Toast';
import type { MapeoVentasPropuesta, RejillaArchivo } from '../api/types';
import { leerLibroCrudo, type HojaCruda } from '../importFile';

/**
 * Importador universal de ventas del TPV. Reunion con Alex: antes que
 * integrarnos con cada TPV, un "traga-archivos" con mapeo de columnas.
 *
 * Dos modos:
 * - "ventas": el export diario de caja → ingresos diarios (upsert por fecha).
 * - "unidades": el ranking de productos vendidos → unidades por plato
 *   para la pestanya de Analisis.
 *
 * Todo el trabajo fino (separadores, numeros con coma, fechas DD/MM,
 * adivinado de columnas) vive en @alexiarest/shared con sus tests; aqui
 * solo se lee el archivo y se pinta el flujo.
 */

export type ImportMode = 'ventas' | 'unidades';

interface WizardProps {
  mode: ImportMode;
  open: boolean;
  onClose: () => void;
  /**
   * Fase 7: de qué fechas son las unidades (modo `unidades`). La carga se
   * guarda con ellas; sin rango, el mes en curso hasta hoy.
   */
  range?: Period;
}

/** Letra de columna al estilo de Excel: 0→A, 25→Z, 26→AA… (lo pidió Alex). */
function columnLetter(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

/** El archivo subido: todas sus hojas tal cual, con las filas vacías. */
interface LibroCargado {
  name: string;
  hojas: HojaCruda[];
}

const vaciaFila = (fila: string[]): boolean => fila.every((c) => (c ?? '').trim() === '');

/**
 * El trozo de hoja que ve Alexia: las 40 primeras filas con algo y las 10
 * últimas (donde suelen ir los totales), 30 columnas como mucho. Con eso se
 * ven títulos, cabecera, datos y totales sin mandarle las ventas del año.
 */
function construirRejilla(hojas: HojaCruda[], indice: number): RejillaArchivo {
  const filas = hojas[indice]?.filas ?? [];
  const conDatos = filas
    .map((celdas, i) => ({ n: i + 1, celdas }))
    .filter((f) => !vaciaFila(f.celdas));
  const elegidas = conDatos.length <= 50 ? conDatos : [...conDatos.slice(0, 40), ...conDatos.slice(-10)];
  const nombres = hojas.map((h) => h.nombre.slice(0, 120)).filter(Boolean).slice(0, 30);
  return {
    hojas: nombres,
    ...(hojas[indice]?.nombre ? { hoja: hojas[indice]!.nombre.slice(0, 120) } : {}),
    totalFilas: Math.max(1, filas.length),
    filas: elegidas.map((f) => ({ n: f.n, celdas: f.celdas.slice(0, 30).map((c) => String(c ?? '').slice(0, 200)) })),
  };
}

/** Mismas cabeceras sin contar las columnas vacías del final. */
function mismasCabeceras(a: string[], b: string[]): boolean {
  const corta = (x: string[]) => {
    const y = x.map((c) => (c ?? '').trim());
    while (y.length > 0 && y[y.length - 1] === '') y.pop();
    return y.join('\u0001');
  };
  return corta(a) === corta(b);
}

const MAX_ROWS = 20_000;

export function ImportSalesWizard({ mode, open, onClose, range }: WizardProps): JSX.Element | null {
  if (!open) return null;
  // El desmontaje al cerrar resetea todo el estado del asistente solo.
  return <WizardBody mode={mode} onClose={onClose} range={range} />;
}

function WizardBody({ mode, onClose, range }: { mode: ImportMode; onClose: () => void; range?: Period }): JSX.Element {
  const rangoUnidades = range ?? nextDishSalesRange(undefined, hoyLocal());
  const toast = useToast();
  const settingsQuery = useSettings();
  const menuItemsQuery = useMenuItems();
  const recipesQuery = useRecipes();
  const { period } = usePeriod();
  const importRevenues = useImportRevenues();
  const importUnits = useImportUnitsSold();
  const { locations, writeLocationId } = useLocationScope();
  // Con varios locales hay que decir donde va a parar: el importador no lee
  // columna de local y en consolidado escribe en el primero (Kilo, oct 2026).
  const destino =
    locations.length > 1
      ? locations.find((l) => l.locationId === writeLocationId)?.name ?? writeLocationId
      : undefined;
  /** Informe de la ultima importacion con dias que no entraron. */
  const [informe, setInforme] = useState<ImportRevenuesResult | null>(null);

  /*
   * Mapeo guardado (Kilo, oct 2026): si este formato de archivo ya se importo
   * antes, llega emparejado como la ultima vez. `recordado` es el que se ha
   * aplicado, para decirlo en pantalla; `exacto` = mismas cabeceras.
   */
  const mappingsQuery = useImportMappings(mode);
  const saveMapping = useSaveImportMapping(mode);
  const [recordado, setRecordado] = useState<{ mapping: SavedImportMapping; exacto: boolean } | null>(null);

  const [libro, setLibro] = useState<LibroCargado | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  /*
   * Dónde está la tabla dentro de la hoja (24 oct 2026): fila de cabecera,
   * filas de total que se saltan y si los días van en filas o en columnas.
   * Lo adivina `detectarEstructura` al subir; Alexia o el usuario lo corrigen.
   */
  const [hojaIdx, setHojaIdx] = useState(0);
  const [estructura, setEstructura] = useState<EstructuraArchivo | null>(null);
  const [saltarTotales, setSaltarTotales] = useState(true);
  /** Si lo leído no cuadra con el total del archivo, importar pide confirmarlo. */
  const [aceptaDescuadre, setAceptaDescuadre] = useState<string | null>(null);
  const [targets, setTargets] = useState<ImportTarget[]>([]);
  const [overwrite, setOverwrite] = useState(true);

  /*
   * Propuesta de Alexia sobre que es cada columna.
   *
   * El adivinador por nombres solo acierta cuando las cabeceras se parecen a
   * las nuestras, y los exports de TPV reales no se parecen a nada. Esto es lo
   * que Alex pedia en vez de la via "conviertelo con ChatGPT": el modelo NO
   * genera un archivo, mira el que ya has subido y PROPONE etiquetas, aqui
   * mismo, donde puedes cambiarlas antes de importar (2 sep 2026).
   *
   * Los motivos se guardan por columna para pintarlos al lado del selector: si
   * no se ve por que ha decidido cada cosa, revisar la propuesta es tan caro
   * como hacer el mapeo a mano.
   */
  const mapeo = useMapeoVentas();
  const [propuesta, setPropuesta] = useState<MapeoVentasPropuesta | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /*
   * Versión de la tabla: sube con cada archivo, hoja o estructura nueva. Una
   * propuesta de Alexia que llega tarde (el usuario ya cambió de archivo) se
   * descarta en vez de aplicarse a otro archivo (revisión de código, 27 oct).
   */
  const versionTabla = useRef(0);

  const platforms = useMemo(
    () => (settingsQuery.data?.platforms ?? []).filter((p) => p.active),
    [settingsQuery.data],
  );
  const menuItems = menuItemsQuery.data ?? [];
  // El codigo del plato es el de la ficha de su receta (mejora 64): con el,
  // el emparejado no depende de que el nombre coincida letra a letra.
  const platosConCodigo = useMemo(() => {
    const codigoDe = new Map((recipesQuery.data ?? []).map((r) => [r.recipeId, r.code?.trim() || undefined]));
    return menuItems.map((m) => ({ menuItemId: m.menuItemId, name: m.name, previousNames: m.previousNames, code: codigoDe.get(m.recipeId) }));
  }, [menuItems, recipesQuery.data]);

  const raw = libro?.hojas[hojaIdx]?.filas ?? [];
  const archivo = useMemo(
    () => (libro && estructura ? aplicarEstructura(libro.hojas[hojaIdx]?.filas ?? [], estructura, saltarTotales) : null),
    [libro, hojaIdx, estructura, saltarTotales],
  );
  const hasHeader = archivo?.tieneCabecera ?? true;

  /** Cabeceras para pintar: la letra de la columna donde no hay nombre. */
  const cabecerasDe = (a: ReturnType<typeof aplicarEstructura>): string[] =>
    a.cabeceras.map((c, i) => (a.tieneCabecera ? c.trim() || columnLetter(i) : columnLetter(i)));

  /**
   * Pone una estructura y re-empareja las columnas que salen de ella. Todo lo
   * que dependía de la tabla anterior (propuesta, confirmación) se olvida.
   */
  const usarEstructura = (
    hojas: HojaCruda[],
    indice: number,
    est: EstructuraArchivo,
    saltar = saltarTotales,
    reemparejar = false,
  ): string[] => {
    const a = aplicarEstructura(hojas[indice]?.filas ?? [], est, saltar);
    const hs = cabecerasDe(a);
    versionTabla.current++;
    setHojaIdx(indice);
    setEstructura(est);
    setAceptaDescuadre(null);
    /*
     * Si las columnas son las mismas (p. ej. solo cambia «saltar totales»),
     * se conserva el emparejado: el del usuario o el de Alexia.
     */
    if (!reemparejar && mismasCabeceras(hs, headers) && hs.length === targets.length) return hs;
    setTargets(emparejar(hs, a.tieneCabecera));
    setPropuesta(null);
    return hs;
  };

  /**
   * ¿Este archivo es un formato ya importado? Se prueba la estructura que se
   * guardó con cada formato: si con ella salen las mismas cabeceras, se usa
   * (es lo que hace que el informe con título de cada mes llegue ya listo).
   */
  const formatoRecordado = (hojas: HojaCruda[]): { indice: number; est: EstructuraArchivo } | null => {
    for (const m of mappingsQuery.data ?? []) {
      if (!m.estructura) continue;
      const indice = m.estructura.hoja ? hojas.findIndex((h) => h.nombre === m.estructura!.hoja) : elegirHoja(hojas);
      if (indice < 0) continue;
      const filas = hojas[indice]!.filas;
      const est = detectarEstructura(filas, {
        orientacion: m.estructura.orientacion,
        filaCabecera: m.estructura.filaCabecera,
      });
      const a = aplicarEstructura(filas, est);
      // Con las MISMAS cabeceras que se guardaron (la letra donde no hay nombre).
      if (a.tieneCabecera && headerSignature(cabecerasDe(a)) === m.signature) return { indice, est };
    }
    return null;
  };

  const onPickFile = async (picked: File | undefined) => {
    if (!picked) return;
    setFileError(null);
    setReading(true);
    try {
      const hojas = await leerLibroCrudo(picked);
      if (hojas.length === 0) throw new Error('sin hojas');
      const recordado = formatoRecordado(hojas);
      const indice = recordado?.indice ?? elegirHoja(hojas);
      const filas = hojas[indice]!.filas;
      if (filas.filter((f) => !vaciaFila(f)).length < 2) {
        setFileError('El archivo está vacío o solo tiene una fila. Exporta los datos con una fila por día (o por producto) e inténtalo otra vez.');
        setLibro(null);
        return;
      }
      if (filas.length > MAX_ROWS) {
        setFileError(`El archivo tiene más de ${formatNumber(MAX_ROWS)} filas. Trocea el export (por meses, por ejemplo) e importa cada parte.`);
        setLibro(null);
        return;
      }
      setSaltarTotales(true);
      setLibro({ name: picked.name, hojas });
      usarEstructura(hojas, indice, recordado?.est ?? detectarEstructura(filas), true, true);
    } catch (error) {
      setFileError(
        (error as Error)?.message === 'archivo demasiado grande'
          ? 'El archivo pesa más de 25 MB. Exporta solo las ventas (por meses, por ejemplo) e inténtalo otra vez.'
          : 'No se pudo leer el archivo. Comprueba que es un CSV o un Excel (.xlsx) sin contraseña.',
      );
      setLibro(null);
    } finally {
      setReading(false);
    }
  };

  /** Otra hoja del libro: se vuelve a buscar la tabla en ella. */
  const onCambiarHoja = (indice: number) => {
    if (!libro) return;
    usarEstructura(libro.hojas, indice, detectarEstructura(libro.hojas[indice]?.filas ?? []));
  };

  /** El usuario corrige dónde está la tabla: misma forma que la pista de Alexia. */
  const onCorregirEstructura = (pista: { orientacion?: 'filas' | 'columnas'; filaCabecera?: number | null }) => {
    if (!libro || !estructura) return;
    usarEstructura(
      libro.hojas,
      hojaIdx,
      detectarEstructura(raw, { orientacion: pista.orientacion ?? estructura.orientacion, ...pista }),
    );
  };

  /**
   * Adivina el mapeo y, si el formato ya se importo antes, lo pisa con lo que
   * se eligio entonces. Sin cabecera no hay nombres con los que reconocerlo.
   */
  const emparejar = (headers: string[], headered: boolean, usarGuardado = true): ImportTarget[] => {
    const adivinado =
      mode === 'ventas' ? guessRevenueColumns(headers, platforms) : guessUnitsColumns(headers);
    const pick = headered && usarGuardado ? pickSavedMapping(mappingsQuery.data ?? [], headers) : null;
    if (!pick) {
      setRecordado(null);
      return adivinado;
    }
    const opciones = targetOptions(mode, platforms);
    setRecordado({ mapping: pick.mapping, exacto: pick.exact });
    // La respuesta del usuario manda: lo guardado solo rellena si aún no ha contestado.
    if (mode === 'ventas' && pick.mapping.amountsIncludeVat !== undefined && conIva === null) {
      setConIva(pick.mapping.amountsIncludeVat ? 'con' : 'sin');
    }
    // Un destino que ya no existe (una plataforma borrada) vuelve a adivinarse.
    return adivinado.map((t, i) => {
      const key = pick.keys[i];
      return (key && opciones.find((o) => o.key === key)?.target) || t;
    });
  };

  /** Tras una importacion que ha entrado: recordar el formato para la proxima. */
  const recordarFormato = () => {
    if (!libro || !estructura || !hasHeader) return;
    saveMapping.mutate({
      headers,
      columns: Object.fromEntries(headers.map((h, i) => [h, targets[i] ? targetKey(targets[i]) : 'ignore'])),
      ...(mode === 'ventas' && conIva ? { amountsIncludeVat: conIva === 'con' } : {}),
      fileName: libro.name,
      estructura: {
        ...(libro.hojas.length > 1 && libro.hojas[hojaIdx]?.nombre ? { hoja: libro.hojas[hojaIdx]!.nombre.slice(0, 120) } : {}),
        orientacion: estructura.orientacion,
        filaCabecera: estructura.filaCabecera,
      },
    });
  };

  /** Pide la propuesta y la aplica al mapeo, dejando los motivos a la vista. */
  const pedirPropuesta = () => {
    if (!libro || !archivo) return;
    const opciones = targetOptions(mode, platforms).filter((o) => o.key !== 'ignore');
    // En ventas, Alexia ve la hoja tal cual y dice también dónde está la tabla.
    const rejilla = mode === 'ventas' ? construirRejilla(libro.hojas, hojaIdx) : undefined;
    const version = versionTabla.current;
    mapeo.mutate(
      {
        columnas: headers,
        // Pocas filas: al modelo le basta con ver de que tipo es cada columna,
        // y mandar el archivo entero seria mandarle las ventas del año.
        muestra: dataRows.slice(0, 5).map((fila) => fila.map((c) => String(c ?? ''))),
        destinos: opciones.map((o) => ({ key: o.key, label: o.label })),
        ...(rejilla ? { rejilla } : {}),
      },
      {
        onSuccess: (res) => {
          if (versionTabla.current !== version) return;
          let ancho = headers.length;
          if (res.estructura) {
            const e = res.estructura;
            const est = detectarEstructura(raw, {
              ...(e.orientacion ? { orientacion: e.orientacion } : {}),
              ...(e.filaCabecera !== undefined ? { filaCabecera: e.filaCabecera } : {}),
              excluir: e.excluir,
              ...(e.filaTotal !== undefined ? { filaTotal: e.filaTotal } : {}),
            });
            const a = aplicarEstructura(raw, est, saltarTotales);
            ancho = cabecerasDe(a).length;
            usarEstructura(libro.hojas, hojaIdx, est, saltarTotales, true);
            /*
             * Los índices de la propuesta son de las cabeceras que vio Alexia
             * en su trozo de hoja. Si en el archivo entero salen otras (más
             * columnas, otra fila de etiquetas), no se aplican a ciegas.
             */
            if (!res.cabeceras || !mismasCabeceras(res.cabeceras, a.cabeceras)) {
              setPropuesta({ ...res, columnas: [] });
              toast.notify('Alexia ha encontrado la tabla. Revisa qué es cada columna antes de importar.');
              return;
            }
          }
          setPropuesta(res);
          setTargets((prev) => {
            const next: ImportTarget[] = res.estructura
              ? Array.from({ length: ancho }, () => ({ kind: 'ignore' }) as ImportTarget)
              : [...prev];
            for (const col of res.columnas) {
              if (col.indice >= next.length) continue;
              const opcion = opciones.find((o) => o.key === col.destino);
              // Lo que el modelo no ha sabido decidir se queda sin importar,
              // nunca en el destino que hubiera adivinado el nombre: mezclar
              // las dos fuentes haria imposible saber quien decidio que.
              next[col.indice] = opcion ? opcion.target : { kind: 'ignore' };
            }
            return next;
          });
          toast.notify('Revisa la propuesta antes de importar: cámbiala donde no cuadre.');
        },
        onError: (error) => toast.fail(error),
      },
    );
  };

  const headers = useMemo(() => (archivo ? cabecerasDe(archivo) : []), [archivo]);
  const dataRows = useMemo(() => archivo?.filas ?? [], [archivo]);

  /** «El archivo trae cabecera»: apagado, la tabla empieza en los datos. */
  const onToggleHeader = (checked: boolean) => {
    if (!libro || !estructura) return;
    if (!checked) {
      onCorregirEstructura({ filaCabecera: null });
      return;
    }
    const auto = detectarEstructura(raw, { orientacion: estructura.orientacion });
    if (auto.filaCabecera !== null) {
      usarEstructura(libro.hojas, hojaIdx, auto);
      return;
    }
    // La detección no ve cabecera: la primera fila con algo hace de cabecera.
    onCorregirEstructura({ filaCabecera: Math.max(0, raw.findIndex((f) => !vaciaFila(f))) });
  };

  /*
   * Si los importes del TPV vienen con IVA o sin él. Arranca SIN responder a
   * propósito: darlo por hecho es lo que infla la facturación, el ticket medio
   * y todos los márgenes de golpe, y encima es invisible después. Hasta que no
   * se responde, el botón de importar está apagado.
   */
  const [conIva, setConIva] = useState<'sin' | 'con' | null>(null);
  const vatPct = settingsQuery.data?.vatPct ?? 10;

  const revenuePreview = useMemo(
    () =>
      mode === 'ventas' && dataRows.length
        ? buildRevenueImport(dataRows, targets, {
            amountsIncludeVat: conIva === 'con',
            vatPct,
          })
        : null,
    [mode, dataRows, targets, conIva, vatPct],
  );
  const unitsPreview = useMemo(
    () =>
      mode === 'unidades' && dataRows.length
        ? buildUnitsImport(dataRows, targets, platosConCodigo)
        : null,
    [mode, dataRows, targets, platosConCodigo],
  );

  /*
   * Cuadre con el total del propio archivo (punto 3 de Alex): si lo que vamos
   * a leer no suma lo que dice su fila de total, algo se ha quedado fuera o
   * se ha contado dos veces. Se compara con los números tal cual (con o sin
   * IVA, como vengan): es una comprobación de lectura, no del análisis.
   */
  const cuadre = useMemo(
    () => (mode === 'ventas' && archivo ? cuadrarConTotal(archivo, targets) : null),
    [mode, archivo, targets],
  );
  /** Un descuadre aceptado vale para ESE descuadre: si cambia el mapeo, se vuelve a preguntar. */
  const firmaCuadre = cuadre ? JSON.stringify(cuadre.columnas.map((c) => [c.indice, c.diferencia])) : '';
  const descuadrePendiente =
    cuadre?.estado === 'doble_conteo' || (cuadre?.estado === 'no_cuadra' && aceptaDescuadre !== firmaCuadre);

  // ¿Que dias del archivo ya tienen datos guardados? Para avisar antes de pisar.
  const importRange = useMemo(() => {
    const days = revenuePreview?.days ?? [];
    if (days.length === 0) return null;
    return { start: days[0].date, end: days[days.length - 1].date };
  }, [revenuePreview]);
  const existingQuery = useQuery({
    queryKey: importRange ? keys.revenues(importRange) : ['revenues', 'none'],
    queryFn: () => api.listRevenues(importRange!),
    enabled: importRange !== null,
  });
  const existingDates = useMemo(() => {
    const dates = new Set<string>();
    for (const day of existingQuery.data ?? []) {
      const total =
        Object.values(day.inHouse ?? {}).reduce((a, b) => a + (b ?? 0), 0) +
        Object.values(day.delivery ?? {}).reduce((a, b) => a + (b ?? 0), 0);
      if (total !== 0) dates.add(day.date);
    }
    return dates;
  }, [existingQuery.data]);

  const daysToImport = useMemo(() => {
    const days = revenuePreview?.days ?? [];
    return overwrite ? days : days.filter((day) => !existingDates.has(day.date));
  }, [revenuePreview, overwrite, existingDates]);
  const collidingCount = (revenuePreview?.days ?? []).filter((d) =>
    existingDates.has(d.date),
  ).length;

  const importTotal = useMemo(
    () =>
      daysToImport.reduce(
        (sum, day) =>
          sum +
          Object.values(day.inHouse ?? {}).reduce((a, b) => a + (b ?? 0), 0) +
          Object.values(day.delivery ?? {}).reduce((a, b) => a + (b ?? 0), 0),
        0,
      ),
    [daysToImport],
  );

  const busy = importRevenues.isPending || importUnits.isPending;

  const onImport = () => {
    if (mode === 'ventas') {
      importRevenues.mutate(daysToImport, {
        onSuccess: (result) => {
          if (result.saved > 0) recordarFormato();
          if (result.fallidos.length === 0) {
            toast.success(`Importados ${result.saved} días de ventas (${formatMoney(importTotal)}).`);
            onClose();
            return;
          }
          // Con dias fallidos el cajon se queda abierto con el informe: un
          // toast de 6 segundos no deja ver cuales son.
          setInforme(result);
        },
        onError: (error) => toast.fail(error),
      });
    } else if (unitsPreview) {
      const byId = new Map(menuItems.map((item) => [item.menuItemId, item]));
      const rows = unitsPreview.matched
        .map((match) => ({ item: byId.get(match.menuItemId), units: match.units }))
        .filter((row): row is { item: MenuItem; units: number } => row.item !== undefined);
      importUnits.mutate({ rows, range: rangoUnidades, source: 'excel' }, {
        onSuccess: (saved) => {
          if (saved > 0) recordarFormato();
          toast.success(`Unidades vendidas actualizadas en ${saved} platos.`);
          onClose();
        },
        onError: (error) => toast.fail(error),
      });
    }
  };

  /** Reintenta solo los dias que fallaron y junta el resultado con el anterior. */
  const onRetryFailed = () => {
    if (!informe) return;
    const fallidas = new Set(informe.fallidos.map((f) => f.date));
    const dias = daysToImport.filter((day) => fallidas.has(day.date));
    importRevenues.mutate(dias, {
      onSuccess: (result) => {
        const merged = {
          ...informe,
          saved: informe.saved + result.saved,
          fallidos: result.fallidos,
        };
        if (merged.fallidos.length === 0) {
          toast.success(`Importados los ${merged.total} días de ventas.`);
          onClose();
          return;
        }
        setInforme(merged);
      },
      onError: (error) => toast.fail(error),
    });
  };

  const canImport =
    mode === 'ventas'
      ? daysToImport.length > 0 && conIva !== null && !descuadrePendiente
      : (unitsPreview?.matched.length ?? 0) > 0;

  return (
    <Drawer
      open
      onClose={busy ? () => undefined : onClose}
      title={mode === 'ventas' ? 'Importar ventas del punto de venta' : 'Importar unidades vendidas'}
      subtitle={
        mode === 'ventas'
          ? 'Sube el export de caja (CSV o Excel) con una fila por día. Te preguntamos si los importes llevan IVA: aquí se guardan siempre sin él.'
          : `Sube el ranking de productos vendidos del punto de venta (CSV o Excel). Se guardan como tus ventas ${rangoEnPalabras(rangoUnidades.start, rangoUnidades.end)}.`
      }
      width="42rem"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={onImport}
            disabled={!canImport || busy}
          >
            {busy
              ? 'Importando…'
              : mode === 'ventas'
                ? `Importar ${daysToImport.length} ${daysToImport.length === 1 ? 'día' : 'días'}`
                : `Importar ${unitsPreview?.matched.length ?? 0} platos`}
          </button>
        </>
      }
    >
      <div className="form">
        {informe && (
          <ImportReport informe={informe} destino={destino} busy={busy} onRetry={onRetryFailed} />
        )}
        <div className="import-file">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv"
            className="visually-hidden"
            onChange={(event) => void onPickFile(event.target.files?.[0])}
          />
          <button
            type="button"
            className="btn"
            onClick={() => inputRef.current?.click()}
            disabled={reading || busy}
          >
            {reading ? 'Leyendo el archivo…' : libro ? 'Cambiar archivo' : 'Elegir archivo'}
          </button>
          {libro && (
            <span className="import-file__name">
              {libro.name} · {formatNumber(dataRows.length)} filas
            </span>
          )}
        </div>
        {fileError && <p className="form__error" role="alert">{fileError}</p>}

        {mode === 'ventas' && <AyudaOtroTpv plataformas={platforms.map((p) => p.name)} />}
        {mode === 'unidades' && (
          <PlantillaUnidades platos={platosConCodigo} periodo={period} />
        )}

        {libro && archivo && estructura && (
          <>
            <EstructuraTabla
              libro={libro}
              hojaIdx={hojaIdx}
              estructura={estructura}
              archivo={archivo}
              saltarTotales={saltarTotales}
              hasHeader={hasHeader}
              onHoja={onCambiarHoja}
              onCorregir={onCorregirEstructura}
              onToggleHeader={onToggleHeader}
              onSaltarTotales={(v) => {
                setSaltarTotales(v);
                if (libro) usarEstructura(libro.hojas, hojaIdx, estructura, v);
              }}
              hojaSugerida={propuesta?.estructura?.hojaSugerida}
            />

            {mode === 'ventas' && (
              <div className="form__section">
                <p className="form__section-title">Los importes de este archivo…</p>
                <p className="form__section-hint">
                  Alexia analiza las ventas <strong>sin IVA</strong>. Cada punto de venta exporta una cosa:
                  «Ventas netas» suele venir ya sin impuestos y «Total del día» casi nunca. No lo
                  damos por supuesto porque, si nos equivocamos, la facturación y todos los
                  márgenes quedan inflados y no hay forma de notarlo después.
                </p>
                <div className="row">
                  <label className="radio-inline">
                    <input
                      type="radio"
                      name="importes-iva"
                      checked={conIva === 'sin'}
                      onChange={() => setConIva('sin')}
                    />
                    Ya están sin IVA
                  </label>
                  <label className="radio-inline">
                    <input
                      type="radio"
                      name="importes-iva"
                      checked={conIva === 'con'}
                      onChange={() => setConIva('con')}
                    />
                    Incluyen IVA ({vatPct} %)
                  </label>
                </div>
                {conIva === null && (
                  <p className="field__hint">
                    Elige una de las dos para poder importar.
                  </p>
                )}
              </div>
            )}

            <div className="form__section">
              <p className="form__section-title">Qué es cada columna</p>
              <p className="form__section-hint">
                Lo hemos adivinado por los nombres de las columnas; corrige lo que no cuadre.
                Lo que marques como «No importar» se ignora.
              </p>
              {mode === 'ventas' && (
                <p className="form__section-hint">
                  Usa <strong>«Ventas totales del día»</strong> si tu archivo trae una sola
                  columna con todas las ventas, sin separar por canal ni por método de pago.
                </p>
              )}

              {recordado && (
                <p className="form__note" role="status">
                  Emparejado como la última vez que subiste este formato
                  {recordado.mapping.fileName ? ` («${recordado.mapping.fileName}»` : ' ('}
                  {`, ${formatDate(recordado.mapping.usedAt.slice(0, 10))})`}
                  {recordado.exacto ? '.' : '; las columnas nuevas se han adivinado.'}{' '}
                  Revisa la vista previa y, si cuadra, importa.{' '}
                  <button
                    type="button"
                    className="btn btn--link btn--sm"
                    onClick={() => setTargets(emparejar(headers, hasHeader, false))}
                  >
                    Emparejar de cero
                  </button>
                </p>
              )}

              {/*
                * La propuesta de Alexia. No importa nada: rellena este mismo
                * mapeo para que lo revises. Por eso el botón está aquí arriba
                * y no en su propia pantalla.
                */}
              <div className="row">
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={pedirPropuesta}
                  disabled={mapeo.isPending}
                >
                  {mapeo.isPending
                    ? 'Mirando tu archivo…'
                    : propuesta
                      ? 'Volver a proponer'
                      : '¿Te lo emparejamos? Pídeselo a Alexia'}
                </button>
                {propuesta && (
                  <span className="muted small">
                    Propuesta aplicada. <strong>Revísala</strong> antes de importar.
                  </span>
                )}
              </div>

              {propuesta?.ivaPista && propuesta.ivaPista.valor !== 'no_se' && (
                <p className="form__note" role="note">
                  Por lo que se ve en el archivo, los importes parecen venir{' '}
                  <strong>
                    {propuesta.ivaPista.valor === 'con_iva' ? 'con IVA' : 'ya sin IVA'}
                  </strong>
                  {propuesta.ivaPista.motivo ? `: ${propuesta.ivaPista.motivo}` : '.'} Aun así,
                  la respuesta de arriba la das tú.
                </p>
              )}

              {(propuesta?.avisos.length ?? 0) > 0 && (
                <ul className="form__section-hint">
                  {propuesta!.avisos.map((aviso, i) => (
                    <li key={i}>{aviso}</li>
                  ))}
                </ul>
              )}
              <table className="table table--compact import-map">
                <thead>
                  <tr>
                    <th scope="col">Columna</th>
                    <th scope="col">Ejemplo</th>
                    <th scope="col">Se importa como</th>
                  </tr>
                </thead>
                <tbody>
                  {headers.map((header, index) => {
                    const letra = columnLetter(index);
                    return (
                    <tr key={index}>
                      <th scope="row">
                        <span className="import-map__col">{letra}</span>
                        {header !== letra && <span className="import-map__name">{header}</span>}
                      </th>
                      <td className="import-map__sample">
                        {dataRows
                          .slice(0, 2)
                          .map((row) => row[index])
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </td>
                      <td>
                        <TargetSelect
                          mode={mode}
                          platforms={platforms}
                          value={targets[index] ?? { kind: 'ignore' }}
                          header={header}
                          onChange={(target) => {
                            setTargets((prev) => {
                              const next = [...prev];
                              next[index] = target;
                              return next;
                            });
                          }}
                        />
                        {(() => {
                          // El porqué de la propuesta, al lado de lo propuesto:
                          // sin motivo, revisarla cuesta lo mismo que hacerlo
                          // a mano y nadie la revisa.
                          const col = propuesta?.columnas.find((c) => c.indice === index);
                          if (!col?.motivo) return null;
                          return (
                            <span
                              className={`cell-sub ${col.confianza === 'baja' ? 'neg' : ''}`.trim()}
                            >
                              {col.confianza === 'baja' ? '⚠ ' : ''}
                              {col.motivo}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {mode === 'ventas' && cuadre && revenuePreview && (
              <CuadreArchivo
                cuadre={cuadre}
                pendiente={descuadrePendiente}
                onAceptar={() => setAceptaDescuadre(firmaCuadre)}
              />
            )}
            {mode === 'ventas' && revenuePreview && (
              <RevenuePreview
                preview={revenuePreview}
                daysToImport={daysToImport.length}
                total={importTotal}
                colliding={collidingCount}
                overwrite={overwrite}
                onOverwrite={setOverwrite}
                destino={destino}
                filaDeDatos={hasHeader ? 2 : 1}
                origen={archivo.origen}
              />
            )}
            {mode === 'unidades' && unitsPreview && <UnitsPreview preview={unitsPreview} />}
          </>
        )}
      </div>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/* Select de destino de columna                                        */
/* ------------------------------------------------------------------ */

interface TargetOption {
  key: string;
  label: string;
  target: ImportTarget;
}

/**
 * "¿Tu archivo no encaja?" — la salida de emergencia, no el camino principal.
 *
 * Nacio como version sencilla de la mejora 10: nuestro formato mas un prompt
 * que hace la conversion en cualquier asistente. Probandolo con exports reales
 * de TPV, Alex vio que el modelo se equivoca emparejando columnas y que el
 * archivo puede parecer correcto estandolo mal (2 sep 2026) — y ahi el fallo
 * ya no se ve: entra en Alexia y todo el analisis sale de datos falsos.
 *
 * Asi que la pantalla dice lo que es. El mecanismo bueno sigue siendo el mapeo
 * de columnas de este mismo importador, donde el usuario VE que es cada cosa
 * antes de importar; esto es para quien no puede tocar su export de ninguna
 * otra forma, y con el aviso de comprobar el resultado.
 */
/**
 * Plantilla de unidades vendidas (mejora 64 de Alex): sale con la carta ya
 * puesta (codigo y nombre), solo hay que rellenar las unidades del periodo
 * seleccionado. Flujo: periodo → plantilla → unidades → importar → revisar.
 */
function PlantillaUnidades({
  platos,
  periodo,
}: {
  platos: Array<{ code?: string; name: string }>;
  periodo: { start: string; end: string };
}): JSX.Element {
  const descargar = async () => {
    const { descargarPlantillaUnidades } = await import('../importFile');
    await descargarPlantillaUnidades(UNIDADES_TEMPLATE_HEADERS, platos, periodo.start.slice(0, 7));
  };
  return (
    <div className="form__section">
      <p className="form__section-title">¿Tu punto de venta no exporta los datos con este formato?</p>
      <p className="form__section-hint">
        Descarga la plantilla y copia tus ventas respetando estas columnas:{' '}
        <strong>{UNIDADES_TEMPLATE_HEADERS.join(' | ')}</strong>. Sale con tu carta ya puesta; solo
        tienes que llenar las unidades. Si el código coincide, el plato se empareja por él aunque
        el nombre no sea idéntico.
      </p>
      <p className="form__section-hint">
        Las unidades son el <strong>acumulado del periodo que tienes seleccionado</strong> (
        {formatPeriod(periodo.start, periodo.end)}), no ventas diarias: es lo que necesita la
        ingeniería de menú.
      </p>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => void descargar()}>
        Descargar plantilla
      </button>
    </div>
  );
}

function AyudaOtroTpv({ plataformas }: { plataformas: string[] }): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const toast = useToast();
  const prompt = promptAdaptarVentas(plataformas);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      toast.notify('No hemos podido copiarlo. Selecciónalo y cópialo a mano.', 'error');
    }
  };

  const descargar = async () => {
    const { descargarPlantillaVentas } = await import('../importFile');
    await descargarPlantillaVentas(VENTAS_TEMPLATE_HEADERS, VENTAS_TEMPLATE_ROWS);
  };

  if (!abierto) {
    return (
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAbierto(true)}>
        ¿Tu archivo no encaja de ninguna manera?
      </button>
    );
  }

  return (
    <div className="form__section">
      <p className="form__section-title">Adaptar el archivo de tu punto de venta</p>
      <p className="form__section-hint">
        <strong>Antes de esto:</strong> casi siempre no hace falta. Tu archivo se puede importar
        tal cual diciendo aquí arriba qué es cada columna, y así ves el emparejamiento antes de
        importar nada.
      </p>
      <p className="form__section-hint">
        Si aun así tu export no hay por dónde cogerlo, descarga nuestra plantilla y pide a
        ChatGPT (o al asistente que uses) que te convierta el archivo. El texto de abajo lleva
        las reglas que importan: la plantilla como formato de destino, importes sin IVA, sin
        inventarse datos y con el emparejamiento declarado al final.
      </p>
      <p className="form__note" role="note">
        Un asistente puede equivocarse asignando columnas y devolverte un archivo que parece
        correcto. Cuando lo subas, <strong>comprueba el mapeo y la vista previa</strong> antes de
        importar: si las ventas entran mal, después no hay forma de notarlo.
      </p>
      <ol className="excel-steps">
        <li>
          <p>
            <strong>Descarga la plantilla.</strong> Es el formato que entendemos sin que tengas
            que emparejar columnas.
          </p>
          <button type="button" className="btn btn--sm" onClick={() => void descargar()}>
            Descargar plantilla
          </button>
        </li>
        <li>
          <p>
            <strong>Copia este texto</strong> y pégalo junto a los dos archivos (el tuyo y la
            plantilla).
          </p>
          <pre className="prompt-box">{prompt}</pre>
          <button type="button" className="btn btn--sm" onClick={() => void copiar()}>
            {copiado ? 'Copiado ✓' : 'Copiar el texto'}
          </button>
        </li>
        <li>
          <p>
            <strong>Vuelve aquí</strong> con el archivo que te devuelva, súbelo y{' '}
            <strong>revisa el mapeo y la vista previa</strong> antes de importar. Compáralos con
            el resumen de emparejamiento que te haya dado el asistente.
          </p>
        </li>
      </ol>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAbierto(false)}>
        Cerrar
      </button>
    </div>
  );
}

function targetOptions(mode: ImportMode, platforms: DeliveryPlatform[]): TargetOption[] {
  if (mode === 'unidades') {
    return [
      { key: 'ignore', label: 'No importar', target: { kind: 'ignore' } },
      { key: 'code', label: 'Código del plato', target: { kind: 'code' } },
      { key: 'dish', label: 'Nombre del plato', target: { kind: 'dish' } },
      { key: 'units', label: 'Unidades vendidas', target: { kind: 'units' } },
    ];
  }
  return [
    { key: 'ignore', label: 'No importar', target: { kind: 'ignore' } },
    { key: 'date', label: 'Fecha', target: { kind: 'date' } },
    /*
     * "Total del día (sin desglose)" no decia de que era el total y la coletilla
     * se comia el nombre (Alex, 2 sep 2026). Que no lleva desglose se explica
     * arriba, en la ayuda del mapeo, que es donde cabe.
     */
    { key: 'total', label: 'Ventas totales del día', target: { kind: 'total' } },
    ...(Object.keys(PAYMENT_METHOD_LABELS) as Array<keyof typeof PAYMENT_METHOD_LABELS>).map(
      (method) => ({
        key: `inHouse:${method}`,
        label: `Local · ${PAYMENT_METHOD_LABELS[method]}`,
        target: { kind: 'inHouse', method } as ImportTarget,
      }),
    ),
    ...platforms.map((platform) => ({
      key: `delivery:${platform.platformId}`,
      label: `Domicilio · ${platform.name}`,
      target: { kind: 'delivery', platformId: platform.platformId } as ImportTarget,
    })),
    { key: 'stat:customers', label: 'Comensales', target: { kind: 'stat', name: 'customers' } },
    {
      key: 'avgTicket',
      label: 'Ticket promedio (calcula los comensales)',
      target: { kind: 'avgTicket' },
    },
    { key: 'stat:tables', label: 'Mesas', target: { kind: 'stat', name: 'tables' } },
    { key: 'stat:deliveryOrders', label: 'Pedidos a domicilio', target: { kind: 'stat', name: 'deliveryOrders' } },
    { key: 'stat:courtesies', label: 'Cortesías', target: { kind: 'stat', name: 'courtesies' } },
    { key: 'stat:discounts', label: 'Descuentos', target: { kind: 'stat', name: 'discounts' } },
  ];
}

function TargetSelect({
  mode,
  platforms,
  value,
  header,
  onChange,
}: {
  mode: ImportMode;
  platforms: DeliveryPlatform[];
  value: ImportTarget;
  header: string;
  onChange: (target: ImportTarget) => void;
}): JSX.Element {
  const options = targetOptions(mode, platforms);
  const currentKey = targetKey(value);
  // Un destino adivinado que no este entre las opciones (p. ej. una
  // plataforma desactivada) se ensenya igual para no perderlo.
  const known = options.some((option) => option.key === currentKey);
  return (
    <select
      className="input input--compact"
      value={currentKey}
      aria-label={`Destino de la columna ${header}`}
      onChange={(event) => {
        const next = options.find((option) => option.key === event.target.value);
        if (next) onChange(next.target);
      }}
    >
      {!known && <option value={currentKey}>{currentKey}</option>}
      {options.map((option) => (
        <option key={option.key} value={option.key}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/* ------------------------------------------------------------------ */
/* Previsualizaciones                                                  */
/* ------------------------------------------------------------------ */

function RevenuePreview({
  preview,
  daysToImport,
  total,
  colliding,
  overwrite,
  onOverwrite,
  destino,
  filaDeDatos,
  origen,
}: {
  preview: NonNullable<ReturnType<typeof buildRevenueImport>>;
  daysToImport: number;
  total: number;
  colliding: number;
  overwrite: boolean;
  onOverwrite: (value: boolean) => void;
  /** Nombre del local destino; solo si hay varios. */
  destino?: string;
  /** Numero de fila en la hoja de la primera fila de datos (1 sin cabecera, 2 con). */
  filaDeDatos: number;
  /** Dónde estaba cada fila en el archivo («fila 12», «columna D»). Manda sobre `filaDeDatos`. */
  origen?: string[];
}): JSX.Element {
  const { days, skippedDetail, warnings } = preview;
  return (
    <div className="form__section">
      <p className="form__section-title">Lo que se va a importar</p>
      {days.length === 0 ? (
        <p className="form__section-hint">
          Con este mapeo no sale ningún día válido. Revisa que haya una columna marcada como
          «Fecha» y al menos una de importes.
        </p>
      ) : (
        <ul className="import-summary">
          <li>
            <strong>{daysToImport}</strong> {daysToImport === 1 ? 'día' : 'días'} ·{' '}
            {days[0].date} → {days[days.length - 1].date}
          </li>
          <li>
            Total: <strong>{formatMoney(total)}</strong> (sin IVA)
          </li>
          {destino && (
            <li>
              Se guardan en el local <strong>{destino}</strong>. Si son de otro, cierra y elige
              ese local en el selector de locales antes de importar.
            </li>
          )}
          <FilasDescartadas filas={skippedDetail} primeraFila={filaDeDatos} origen={origen} />
          {colliding > 0 && (
            <li>
              {colliding} {colliding === 1 ? 'día ya tiene' : 'días ya tienen'} datos guardados.
            </li>
          )}
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
      {colliding > 0 && (
        <CheckboxField
          label="Sobrescribir los días que ya tienen datos"
          hint="Si lo desmarcas, esos días se saltan y se conserva lo que ya había."
          checked={overwrite}
          onChange={onOverwrite}
        />
      )}
    </div>
  );
}

/** Cuantas filas descartadas se listan antes de resumir con «…y N mas». */
const MAX_FILAS_DESCARTADAS = 10;

/**
 * Informe fijo tras una importacion con dias que no han entrado (Kilo, oct
 * 2026). Dice cuantos entraron, cuales no y por que, deja descargarlos y
 * reintentar solo esos. El upsert es idempotente: reintentar no duplica.
 */
function ImportReport({
  informe,
  destino,
  busy,
  onRetry,
}: {
  informe: ImportRevenuesResult;
  destino?: string;
  busy: boolean;
  onRetry: () => void;
}): JSX.Element {
  const { saved, total, fallidos } = informe;
  const descargar = () => {
    const csv = ['fecha;motivo', ...fallidos.map((f) => `${f.date};"${f.reason.replace(/"/g, '""')}"`)].join('\n');
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dias-sin-importar.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="form__section" role="alert">
      <p className="form__section-title">
        Se guardaron {saved} de {total} días{destino ? ` en ${destino}` : ''}. {fallidos.length}{' '}
        {fallidos.length === 1 ? 'no ha entrado' : 'no han entrado'}:
      </p>
      <ul className="import-summary">
        {fallidos.slice(0, MAX_FILAS_DESCARTADAS).map((f) => (
          <li key={f.date}>
            <strong>{formatDate(f.date)}</strong>: {f.reason}
          </li>
        ))}
        {fallidos.length > MAX_FILAS_DESCARTADAS && (
          <li>…y {fallidos.length - MAX_FILAS_DESCARTADAS} más (están todos en el archivo).</li>
        )}
      </ul>
      <div className="row">
        <button type="button" className="btn btn--primary" onClick={onRetry} disabled={busy}>
          {busy ? 'Reintentando…' : `Reintentar solo esos ${fallidos.length} días`}
        </button>
        <button type="button" className="btn" onClick={descargar}>
          Descargar la lista
        </button>
      </div>
    </div>
  );
}

function UnitsPreview({
  preview,
}: {
  preview: NonNullable<ReturnType<typeof buildUnitsImport>>;
}): JSX.Element {
  const { matched, unmatched, skippedRows } = preview;
  return (
    <div className="form__section">
      <p className="form__section-title">Lo que se va a importar</p>
      {matched.length === 0 ? (
        <p className="form__section-hint">
          Ningún producto del archivo coincide con platos de tu carta. Revisa el mapeo de
          columnas y que los nombres se parezcan a los de «Precios de la carta».
        </p>
      ) : (
        <table className="table table--compact">
          <thead>
            <tr>
              <th scope="col">En el archivo</th>
              <th scope="col">Plato de la carta</th>
              <th scope="col" className="is-right">Unidades</th>
            </tr>
          </thead>
          <tbody>
            {matched.map((match) => (
              <tr key={match.menuItemId}>
                <td>{match.importedName}</td>
                <td>
                  {match.menuItemName}
                  {match.porCodigo && (
                    <span className="import-fuzzy" title="Emparejado por el código del plato">
                      {' '}#
                    </span>
                  )}
                  {match.fuzzy && (
                    <span
                      className="import-fuzzy"
                      title="Emparejado por parecido de nombre: revísalo"
                    >
                      {' '}≈
                    </span>
                  )}
                </td>
                <td className="num">{formatNumber(match.units)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {unmatched.length > 0 && (
        <div className="form__section-hint import-sin-pareja" role="alert">
          <p>
            <strong>
              {unmatched.length === 1
                ? 'Un producto del archivo no coincide con ningún plato de la carta'
                : `${unmatched.length} productos del archivo no coinciden con ningún plato de la carta`}
            </strong>
            . No se importan: corrige el código o el nombre en el archivo, o crea el plato en
            «Precios de la carta», y vuelve a subirlo.
          </p>
          <ul className="import-sin-pareja__lista">
            {unmatched.slice(0, 20).map((row) => (
              <li key={`${row.importedCode ?? ''}|${row.importedName}`}>
                {row.importedCode && <code>{row.importedCode}</code>} {row.importedName}
                <span className="muted"> · {formatNumber(row.units)} uds</span>
              </li>
            ))}
            {unmatched.length > 20 && <li>… y {unmatched.length - 20} más</li>}
          </ul>
        </div>
      )}
      {skippedRows > 0 && (
        <p className="form__section-hint">{skippedRows} filas descartadas por no tener nombre, código o número.</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dónde está la tabla y cuadre con el total (24 oct 2026)             */
/* ------------------------------------------------------------------ */

/**
 * Lo que hemos entendido del archivo, en una línea, y cómo corregirlo: la
 * hoja, la fila de cabecera, si los días van en filas o en columnas y qué
 * filas de total se saltan. Es la misma idea que el mapeo de columnas: se ve
 * qué se ha decidido, y se puede cambiar antes de importar.
 */
function EstructuraTabla({
  libro,
  hojaIdx,
  estructura,
  archivo,
  saltarTotales,
  hasHeader,
  onHoja,
  onCorregir,
  onToggleHeader,
  onSaltarTotales,
  hojaSugerida,
}: {
  libro: LibroCargado;
  hojaIdx: number;
  estructura: EstructuraArchivo;
  archivo: ReturnType<typeof aplicarEstructura>;
  saltarTotales: boolean;
  hasHeader: boolean;
  onHoja: (indice: number) => void;
  onCorregir: (pista: { orientacion?: 'filas' | 'columnas'; filaCabecera?: number | null }) => void;
  onToggleHeader: (checked: boolean) => void;
  onSaltarTotales: (value: boolean) => void;
  hojaSugerida?: string;
}): JSX.Element {
  const varias = libro.hojas.length > 1;
  const indiceSugerida = hojaSugerida ? libro.hojas.findIndex((h) => h.nombre === hojaSugerida) : -1;
  const [filaTexto, setFilaTexto] = useState<string | null>(null);
  const filaCabecera = estructura.filaCabecera !== null ? estructura.filaCabecera + 1 : null;
  const descartadas = archivo.descartadas;
  const hayTotales = estructura.excluir.some((r) => r !== estructura.filaTotal);

  return (
    <div className="form__section">
      <p className="form__section-title">Dónde está la tabla</p>
      <p className="form__section-hint">
        {estructura.orientacion === 'columnas'
          ? `Los días van en columnas (fechas en la fila ${filaCabecera ?? 1}): les damos la vuelta para leer un día por fila.`
          : filaCabecera !== null
            ? `Cabecera en la fila ${filaCabecera}; los datos empiezan en la ${estructura.primeraFila + 1}.`
            : `Sin cabecera; los datos empiezan en la fila ${estructura.primeraFila + 1}.`}{' '}
        {descartadas.length > 0 &&
          `No se importan ${descartadas.length} ${descartadas.length === 1 ? 'fila' : 'filas'} de total o subtotal.`}
      </p>

      {indiceSugerida >= 0 && indiceSugerida !== hojaIdx && (
        <p className="form__note" role="status">
          Alexia cree que tus ventas están en la hoja «{hojaSugerida}».{' '}
          <button type="button" className="btn btn--link btn--sm" onClick={() => onHoja(indiceSugerida)}>
            Usar esa hoja
          </button>
        </p>
      )}

      <div className="import-estructura">
        {varias && (
          <label className="field">
            <span className="field__label">Hoja</span>
            <select className="input" value={hojaIdx} onChange={(e) => onHoja(Number(e.target.value))}>
              {libro.hojas.map((h, i) => (
                <option key={i} value={i}>
                  {h.nombre || `Hoja ${i + 1}`}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span className="field__label">Los días van en</span>
          <select
            className="input"
            value={estructura.orientacion}
            onChange={(e) => onCorregir({ orientacion: e.target.value as 'filas' | 'columnas', filaCabecera: undefined })}
          >
            <option value="filas">Filas (un día por fila)</option>
            <option value="columnas">Columnas (un día por columna)</option>
          </select>
        </label>
        {hasHeader && (
          <label className="field">
            <span className="field__label">{estructura.orientacion === 'columnas' ? 'Fechas en la fila' : 'Cabecera en la fila'}</span>
            <input
              className="input input--narrow"
              type="number"
              min={1}
              max={libro.hojas[hojaIdx]?.filas.length ?? 1}
              value={filaTexto ?? String(filaCabecera ?? '')}
              onChange={(e) => setFilaTexto(e.target.value)}
              onBlur={() => {
                const n = Number(filaTexto);
                setFilaTexto(null);
                const max = libro.hojas[hojaIdx]?.filas.length ?? 0;
                if (Number.isInteger(n) && n >= 1 && n <= max && n !== filaCabecera) onCorregir({ filaCabecera: n - 1 });
              }}
            />
          </label>
        )}
      </div>

      {estructura.orientacion === 'filas' && (
        <CheckboxField
          label="El archivo trae una fila de cabecera (nombres de columna)"
          checked={hasHeader}
          onChange={onToggleHeader}
        />
      )}
      {hayTotales && (
        <CheckboxField
          label="Saltar las filas de total y subtotal"
          checked={saltarTotales}
          onChange={onSaltarTotales}
        />
      )}
      {descartadas.length > 0 && (
        // Abierto si son pocas: que se vea sin buscarlo qué se queda fuera.
        <details className="form__section-hint" open={descartadas.length <= 5}>
          <summary>Ver las filas que no se importan</summary>
          <ul>
            {descartadas.slice(0, 20).map((d, i) => (
              <li key={i}>
                {d.origen.charAt(0).toUpperCase() + d.origen.slice(1)}: {d.motivo}
              </li>
            ))}
            {descartadas.length > 20 && <li>…y {descartadas.length - 20} más.</li>}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * ¿Lo leído suma lo mismo que el total del archivo? Si no, importar pide un
 * «sí, importar igualmente» explícito: es la única señal de que se ha colado
 * un subtotal o se ha quedado fuera un día, y después ya no se ve.
 */
function CuadreArchivo({
  cuadre,
  pendiente,
  onAceptar,
}: {
  cuadre: ReturnType<typeof cuadrarConTotal>;
  pendiente: boolean;
  onAceptar: () => void;
}): JSX.Element | null {
  if (cuadre.estado === 'sin_columnas') return null;
  if (cuadre.estado === 'doble_conteo') {
    return (
      <p className="form__error import-cuadre" role="alert">
        Has marcado «Ventas totales del día» y también el desglose (efectivo, tarjeta o plataformas):
        cada día se sumaría dos veces. Deja solo uno de los dos.
      </p>
    );
  }
  if (cuadre.estado === 'sin_total') {
    return (
      <p className="form__section-hint" role="status">
        El archivo no trae una fila de total, así que no podemos cuadrarlo. Compara el total de la vista previa con el de tu TPV.
      </p>
    );
  }
  if (cuadre.estado === 'cuadra') {
    return (
      <p className="form__note" role="status">
        ✓ Cuadra con el total del archivo:{' '}
        {cuadre.columnas.map((c) => `${c.cabecera} ${formatNumber(c.archivo)}`).join(' · ')}.
      </p>
    );
  }
  return (
    <div className="form__error import-cuadre" role="alert">
      <p>
        <strong>No cuadra con el total del archivo.</strong> Revisa las filas que no se importan y qué es cada columna.
      </p>
      <ul>
        {cuadre.columnas
          .filter((c) => !c.cuadra)
          .map((c) => (
            <li key={c.indice}>
              {c.cabecera}: leemos {formatNumber(c.leido)} y el archivo dice {formatNumber(c.archivo)} (
              {c.diferencia > 0 ? 'sobran' : 'faltan'} {formatNumber(Math.abs(c.diferencia))}).
            </li>
          ))}
      </ul>
      {pendiente ? (
        <button type="button" className="btn btn--sm" onClick={onAceptar}>
          Lo he revisado: importar igualmente
        </button>
      ) : (
        <p className="small">Importarás igualmente con esta diferencia.</p>
      )}
    </div>
  );
}
